/**
 * Pure rules behind the product-detail configuration (tier) picker: which of the visible tiers
 * can be bought, and how the campaign's price reads. Two independent sources feed the picker, and
 * they answer different questions:
 *  - the `/configurator` catalog MASK (`is_active` on items and tiers) says what the platform
 *    offers, and therefore what appears on the Product pages (`utils/catalog.visibleTiers`);
 *  - the brand's price-engine CAMPAIGN (`casino-instance/list`, `campaign_data`) says what a
 *    tier costs and whether the campaign currently sells it.
 * A tier the mask offers but the bound campaign does not sell is configuration DRIFT: it stays
 * visible, disabled, and says so, rather than silently disappearing. A brand with no campaign at
 * all has nothing in drift; its tiers are simply unpriced.
 */
import type { CampaignItem, CampaignItemTier, GamificationTier } from '@/components/modules/spark/configurator/types';
import type { GamificationItemWithTiers } from '@/store/slices/configurator-config';

import { isActiveItem, isActiveTier } from '../../utils/catalog';

/** What the brand's campaign says a tier costs. */
export type CampaignPrice = {
  base?: number;
  discounted?: number;
  /** A genuine discount only when a POSITIVE discounted price is set below the base. A
   *  discounted price of 0 is "not priced", not a 100% discount, and a base of 0 can never be
   *  discounted (no "Save Infinity%"). Same rule as the configurator wizard. */
  hasDiscount: boolean;
  /** The figure to show: the discounted price when discounting, else the base. */
  shown?: number;
  /** Whether the campaign puts a positive price on the tier (absent, or 0, is not priced). */
  priced: boolean;
};

export const campaignPrice = (campaignTier: CampaignItemTier | undefined): CampaignPrice => {
  const base = campaignTier?.base_price;
  const discounted = campaignTier?.discounted_price;
  const hasDiscount = base !== undefined && discounted !== undefined && discounted > 0 && discounted < base;
  const shown = hasDiscount ? discounted : base;
  return { base, discounted, hasDiscount, shown, priced: shown !== undefined && shown > 0 };
};

/** How the brand's campaign relates to the item being priced. */
export type CampaignScope = {
  /** The brand has a bound campaign at all. Without one nothing is drift: the tiers are unpriced. */
  present: boolean;
  /** The campaign carries the item and does not disable it at the ITEM level (`CampaignItem.enabled`,
   *  which the price engine honours by excluding the feature from `price_evaluation`). */
  itemEnabled: boolean;
};

/** The {@link CampaignScope} for an item: `present` when the brand has a campaign, `itemEnabled`
 *  when that campaign carries the item and does not flag it disabled. */
export const campaignScopeFor = (present: boolean, campaignItem: CampaignItem | undefined): CampaignScope => ({
  present,
  itemEnabled: campaignItem !== undefined && campaignItem.enabled !== false,
});

export type PlanAvailability = {
  /** Purchasable: the catalog offers the tier (active tier on an active item) AND the bound
   *  campaign carries the item, enables the tier and prices it. */
  available: boolean;
  /** Configuration drift: the catalog offers the tier but the brand's bound campaign does not sell
   *  it (omits the item or the tier, disables either, or prices it at 0). Shown disabled with a
   *  note, to be fixed on the campaign side. Never set for a retired tier or a disabled item (not
   *  offered), nor when the brand has no campaign at all (unpriced, not drift). */
  drift: boolean;
};

export const planAvailability = (
  catalogItem: GamificationItemWithTiers,
  tier: GamificationTier,
  campaignTier: CampaignItemTier | undefined,
  scope: CampaignScope,
): PlanAvailability => {
  const offered = isActiveItem(catalogItem) && isActiveTier(tier);
  const sold = scope.present && scope.itemEnabled && campaignTier?.enabled === true && campaignPrice(campaignTier).priced;
  return { available: offered && sold, drift: offered && scope.present && !sold };
};

/** Whether a plan card shows its struck-through previous price and "Save N%": only for a genuine
 *  discount on a tier that is not drift. A drift card already says "Not priced yet"; a discount
 *  next to that would contradict it. */
export const showsDiscount = (price: CampaignPrice, availability: PlanAvailability): boolean =>
  price.hasDiscount && !availability.drift;
